import type { WorkflowApi } from '../api/WorkflowApi'
import type { WorkflowSchedule, WorkflowScope } from '../models/workflow.types'
import {
  buildScheduleBody,
  validateScheduleDraft,
  type ScheduleDraft,
  type ScheduleFieldErrors,
} from '../service/workflowSchedule'

import { ApiError } from '@/shared/http'

export type SaveScheduleResult =
  | { kind: 'saved'; schedule: WorkflowSchedule }
  | { kind: 'invalid'; fields: ScheduleFieldErrors }
  | { kind: 'error'; error: ApiError; fields: ScheduleFieldErrors }

export interface SaveScheduleFlowOptions {
  api: Pick<WorkflowApi, 'createSchedule' | 'updateSchedule'>
  notifySuccess: (updated: boolean) => void
  reload: () => Promise<void>
}

function apiFieldErrors(error: ApiError): ScheduleFieldErrors {
  const map: Record<string, keyof ScheduleFieldErrors> = {
    frequency: 'frequency',
    local_time: 'localTime',
    timezone: 'timezone',
    day_of_week: 'dayOfWeek',
    day_of_month: 'dayOfMonth',
  }
  return Object.fromEntries(
    Object.entries(error.fields).flatMap(([field, messages]) => {
      const target = map[field]
      const message = messages[0]
      return target && message ? [[target, message]] : []
    }),
  ) as ScheduleFieldErrors
}

export class SaveScheduleFlow {
  constructor(private readonly options: SaveScheduleFlowOptions) {}

  async execute(
    scope: WorkflowScope,
    draft: ScheduleDraft,
    scheduleId?: number | string,
  ): Promise<SaveScheduleResult> {
    const fields = validateScheduleDraft(draft)
    if (Object.keys(fields).length > 0) return { kind: 'invalid', fields }
    try {
      const body = buildScheduleBody(draft)
      const schedule =
        scheduleId === undefined
          ? await this.options.api.createSchedule(scope, body)
          : await this.options.api.updateSchedule(scope, scheduleId, body)
      this.options.notifySuccess(scheduleId !== undefined)
      await this.options.reload()
      return { kind: 'saved', schedule }
    } catch (cause) {
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      return { kind: 'error', error, fields: apiFieldErrors(error) }
    }
  }
}
